// src/prototype/pages/EAPrintableSEPPage.tsx
// ============================================================
// Standalone print-ready Strategic Execution Plan page.
// Route: /sep/print (opens outside AppShell, no sidebar)
//
// Pattern: Identical to EAPrintableReportPage.tsx (PIR)
// Reuses all 10 SEP section components exactly as-is.
// No SEP section logic was changed. No in-app UI was changed.
//
// QI-3 Improvements (March 2026):
//   1. Interactive controls (buttons, selects) hidden in print
//   2. Atomic block protection via sep-atomic utility class
//   3. SEP sub-component cards protected via CSS selectors
//      (rounded-*, inline border-radius, grid/flex children)
//   4. Explicit table/tr/thead print protection for:
//      Scenario Analysis, Resource Plan, Risk Register,
//      and Governance Cadence tables
//   5. h2/h3/h4 break-after: avoid — headers stay with content
//   6. Orphan/widow control on paragraphs
//   7. sep-cover hardened with both page-break and break syntax
//
// QI-Refinement Pass (March 2026) — Trust, Density & Usability:
//   8. "How to Execute This Plan" usage guidance block added before Section 1
//   9. Cross-artifact reference block compressed (~50% shorter)
//  10. Currency format standardized: "$X.XM – $X.XM" throughout
//
// No SEP section logic was changed. No in-app UI was changed.
// ============================================================

import React, { useMemo } from 'react';
import { isQuickWin as badgeIsQuickWin, effortTierForBadge } from '../config/opportunityBadge';
import { Printer, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── Data sources ────────────────────────────────────────────
import { DEMO_CLIENT } from '../config/demoClient';
import { DEMO_OPPORTUNITIES } from '../data/demoOpportunities';

// ── SEP section components (reused exactly as-is) ───────────
import { SEPSectionExecutiveSummary }      from '../components/sep/SEPSectionExecutiveSummary';
import { SEPSectionExecutionStrategy }     from '../components/sep/SEPSectionExecutionStrategy';
import { SEPSectionPortfolioImpact }       from '../components/sep/SEPSectionPortfolioImpact';
import { SEPSectionInitiativePortfolio }   from '../components/sep/SEPSectionInitiativePortfolio';
import { SEPSectionExecutionSequencing }   from '../components/sep/SEPSectionExecutionSequencing';
import { SEPSectionResourcePlan }          from '../components/sep/SEPSectionResourcePlan';
import { SEPSectionDependenciesRisk }      from '../components/sep/SEPSectionDependenciesRisk';
import { SEPSectionGovernance }            from '../components/sep/SEPSectionGovernance';
import { SEPSectionImplementationRoadmap } from '../components/sep/SEPSectionImplementationRoadmap';
import { SEPSectionExecutionRecommendation } from '../components/sep/SEPSectionExecutionRecommendation';

// ── SEPFinalist type (re-imported from orchestrator) ────────
import type { SEPFinalist } from '../components/sep/SEPReportView';

// ── Helpers ─────────────────────────────────────────────────
const toM = (v: number): number => (v == null ? 0 : v < 100 ? v : v / 1_000_000);

const formatDate = (): string =>
  new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/**
 * Maps a raw demoOpportunity to SEPFinalist.
 * Mirrors mapSharedToFinalist in EAActivatePage — no changes to source data.
 */
function mapToSEPFinalist(opp: any, rank: number): SEPFinalist {
  const ROI_MULT = { conservative: 0.75, base: 1.0, aggressive: 1.30 };
  const PAY_MULT = { conservative: 1.30, base: 1.0, aggressive: 0.75 };

  const baseRoiLow  = toM(opp.roiEstimate?.low  ?? 0);
  const baseRoiHigh = toM(opp.roiEstimate?.high ?? 0);
  const basePayback = opp.scenarios?.base?.paybackMonths ?? opp.paybackMonths ?? 12;

  const buildScenario = (key: 'conservative' | 'base' | 'aggressive') => {
    const s = opp.scenarios?.[key];
    if (s?.roiLow != null) {
      return {
        roiLow:        toM(s.roiLow),
        roiHigh:       toM(s.roiHigh),
        paybackMonths: s.paybackMonths ?? Math.round(basePayback * PAY_MULT[key]),
      };
    }
    return {
      roiLow:        parseFloat((baseRoiLow  * ROI_MULT[key]).toFixed(1)),
      roiHigh:       parseFloat((baseRoiHigh * ROI_MULT[key]).toFixed(1)),
      paybackMonths: Math.round(basePayback * PAY_MULT[key]),
    };
  };

  const confRaw = opp.scenarios?.base?.confidence;
  const validationScore =
    typeof confRaw === 'number'
      ? confRaw >= 0.7 ? 85 : confRaw >= 0.4 ? 70 : 55
      : 70;

  return {
    id:    opp.id,
    rank,
    title: opp.title,
    category: opp.category,
    implementationEffort: opp.implementationEffort as 'Low' | 'Medium' | 'High',
    timeToValue: opp.timeToValue ?? '6–9 months',
    // Round to 1 decimal for executive-friendly display — propagates to all child components
    roiEstimate: {
      low:  Math.round(baseRoiLow  * 10) / 10,
      high: Math.round(baseRoiHigh * 10) / 10,
      unit: opp.roiEstimate?.unit ?? 'M',
    },
    validationScore,
    executionRationale: `${opp.title} is selected based on strategic alignment and implementation feasibility. Execution is sequenced by value delivery timeline and organizational capacity.`,
    scenarios: {
      conservative: buildScenario('conservative'),
      base:         buildScenario('base'),
      aggressive:   buildScenario('aggressive'),
    },
  };
}

// ── Component ───────────────────────────────────────────────

/**
 * Artifacts open in a new tab, so there is no history to go "back" through and no router
 * state. The opening surface puts its own path in ?returnTo (see CFOPanel.appendReturnTo);
 * absent that — someone opening the print URL directly — /activate is the right landing.
 * Only same-origin absolute paths are honoured, so the parameter can't be used to bounce a
 * viewer somewhere else.
 */
function returnToFromUrl(): string {
  try {
    const v = new URLSearchParams(window.location.search).get('returnTo');
    if (v && v.startsWith('/') && !v.startsWith('//')) return v;
  } catch {
    /* no-op */
  }
  return '/activate';
}

export default function EAPrintableSEPPage() {
  const navigate = useNavigate();

  // ── Derive finalists from demo data ───────────────────────
  const { finalists, clientName, portfolioRoiLow, portfolioRoiHigh, quickWinCount, executionHorizon } =
    useMemo(() => {
      const sorted = [...DEMO_OPPORTUNITIES].sort(
        (a: any, b: any) => (a.rank ?? 999) - (b.rank ?? 999)
      );
      // EA Preview: top 5 as the demo finalist set
      const top5 = sorted.slice(0, 5);
      const mapped: SEPFinalist[] = top5.map((opp: any, idx: number) =>
        mapToSEPFinalist(opp, idx + 1)
      );

      const roiLow  = mapped.reduce((acc, f) => acc + f.roiEstimate.low,  0);
      const roiHigh = mapped.reduce((acc, f) => acc + f.roiEstimate.high, 0);
      const qwCount = mapped.filter((f: any) => badgeIsQuickWin({ id: f.id, implementationEffort: f.implementationEffort })).length;
      const horizon = mapped.some((f: any) => effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort }) === 'High') ? '12 Months' : '6–9 Months';

      return {
        finalists:        mapped,
        clientName:       DEMO_CLIENT.name,
        portfolioRoiLow:  roiLow,
        portfolioRoiHigh: roiHigh,
        quickWinCount:    qwCount,
        executionHorizon: horizon,
      };
    }, []);

  // ── Set document title for PDF filename default ───────────
  React.useEffect(() => {
    const prev = document.title;
    document.title = `Strategic Execution Plan – ${clientName} – ${formatDate()}`;
    return () => { document.title = prev; };
  }, [clientName]);

  // ── No-ops for callbacks not applicable in print ──────────
  const noop = () => {};

  return (
    <>
      {/* ── Print Stylesheet ─────────────────────────────── */}
      <style>{`
        @media print {
          .sep-no-print { display: none !important; }

          @page {
            size: letter;
            margin: 0.75in 0.65in;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
          }

          /* ── Interactive controls: always hidden in print ── */
          /* Hides "Proceed to Command Center" and any other   */
          /* buttons or select elements in SEP sub-components. */
          button { display: none !important; }
          select { display: none !important; }

          /* ── Section-level page control ──────────────────── */
          .sep-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .sep-section-break {
            page-break-before: always;
            break-before: always;
          }

          /* ── Atomic block protection ──────────────────────── */
          /* Utility class for individual cards, rows, and      */
          /* panels that must not split across pages.           */
          .sep-atomic {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── SEP sub-component card protection ────────────── */
          /* Targets Tailwind-classed card-like elements inside  */
          /* SEP section components without modifying them.      */
          .sep-section [class*="rounded-lg"],
          .sep-section [class*="rounded-xl"],
          .sep-section [class*="rounded-md"],
          .sep-section [class*="rounded-"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Inline-styled card protection ────────────────── */
          /* Catches all divs with inline border-radius.         */
          .sep-section [style*="border-radius"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Grid and flex child protection ──────────────── */
          /* Prevents execution pillar cards, initiative cards,  */
          /* roadmap posture columns, and metric rows from       */
          /* splitting unpredictably at page boundaries.         */
          .sep-section [class*="grid"] > *,
          .sep-section [class*="flex-col"] > *,
          .sep-section [style*="display: grid"] > *,
          .sep-section [style*="display: flex"] > * {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Table protection ─────────────────────────────── */
          /* Protects the Scenario Analysis, Resource Plan,      */
          /* Risk Register, and Governance Cadence tables from   */
          /* splitting mid-row. thead repeats on new pages.      */
          table {
            break-inside: avoid;
            page-break-inside: avoid;
            width: 100%;
          }

          tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          thead {
            display: table-header-group;
          }

          /* ── Heading / content separation ─────────────────── */
          /* Ensures section headers (SECTION N labels + titles) */
          /* always stay visually attached to their content.     */
          h1, h2, h3, h4, h5 {
            break-after: avoid;
            page-break-after: avoid;
          }

          /* ── Orphan / widow control ───────────────────────── */
          p {
            orphans: 3;
            widows: 3;
          }

          /* ── Watermark ────────────────────────────────────── */
          .sep-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }

          .sep-content {
            position: relative;
            z-index: 1;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        @media screen {
          .sep-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }
        }
      `}</style>

      {/* ── Watermark ────────────────────────────────────── */}
      <div className="sep-watermark" aria-hidden="true">
        EARLY ADOPTER PREVIEW
      </div>

      {/* ── Print Toolbar (hidden in print) ──────────────── */}
      <div
        className="sep-no-print sticky top-0 z-50 bg-white border-b border-[#e5e7eb] px-6 py-3 flex items-center justify-between"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(returnToFromUrl())}
            className="flex items-center gap-2 text-sm text-[#717182] hover:text-[#18365E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Execute
          </button>
          <div className="h-5 w-px bg-[#e5e7eb]" />
          <span className="text-sm font-semibold text-[#18365E]">
            Strategic Execution Plan — Print Preview
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white text-sm font-semibold rounded-lg hover:bg-[#18365E]/90 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
          <span className="text-xs text-[#717182]">
            Tip: In Print dialog, select "Save as PDF" for a digital copy
          </span>
        </div>
      </div>

      {/* ── Report Body ──────────────────────────────────── */}
      <div className="sep-content" style={{ fontFamily: 'Montserrat, sans-serif' }}>

        {/* ══════════════════════════════════════════════════ */}
        {/* PAGE 1: BRANDED HEADER + INSIGHT CONTENT           */}
        {/* Replaces the standalone cover — execution           */}
        {/* intelligence starts immediately on page 1.          */}
        {/* ══════════════════════════════════════════════════ */}

        {/* ── Compact branded header strip ──────────────────── */}
        <div
          style={{
            backgroundColor: '#18365E',
            padding: '20px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <img
              src="/public/adaptive_product_logo_white.png"
              alt="Adaptive Product"
              style={{ height: '24px', width: 'auto' }}
            />
            <div style={{ width: '1px', height: '28px', backgroundColor: 'rgba(255,255,255,0.18)' }} />
            <div>
              <p style={{ color: '#FF6E4C', fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', margin: 0 }}>
                Strategic Execution Plan
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', margin: '2px 0 0 0', fontWeight: 500 }}>
                Portfolio Activation · Data Value Accelerator
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: '13px', fontWeight: 700, margin: 0 }}>{clientName}</p>
            <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '11px', margin: '3px 0 0 0' }}>{formatDate()}</p>
          </div>
        </div>

        {/* Coral accent line */}
        <div style={{ height: '3px', backgroundColor: '#FF6E4C' }} />

        {/* ── Page 1 execution intelligence content ─────────── */}
        <div style={{ backgroundColor: '#ffffff', padding: '28px 40px 30px 40px', fontFamily: 'Montserrat, sans-serif' }}>

          {/* Section label */}
          <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#FF6E4C', margin: '0 0 10px 0' }}>
            Execution Intelligence Summary
          </p>

          {/* Lead paragraph */}
          <p style={{ fontSize: '14px', lineHeight: 1.7, color: '#18365E', fontWeight: 600, margin: '0 0 22px 0', maxWidth: '680px' }}>
            This plan sequences {finalists.length} prioritized analytics initiatives for {clientName} into a
            governance-ready execution framework — with a combined projected
            ROI of <strong style={{ color: '#FF6E4C' }}>${portfolioRoiLow.toFixed(1)}M – ${portfolioRoiHigh.toFixed(1)}M</strong>,
            delivering first operational value within {executionHorizon}.
          </p>

          {/* 6 KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '22px' }}>
            {([
              { label: 'Combined Portfolio ROI',   value: `$${portfolioRoiLow.toFixed(1)}M – $${portfolioRoiHigh.toFixed(1)}M`, sub: 'Projected total value'          },
              { label: 'Initiatives Selected',     value: String(finalists.length),                                              sub: 'Shortlisted for execution'     },
              { label: 'Execution Horizon',        value: executionHorizon,                                                     sub: 'Est. time to first value'      },
              { label: 'Quick Wins',               value: String(quickWinCount),                                                sub: 'Low-effort, high-value'        },
              { label: 'Planning Confidence',      value: 'High',                                                               sub: 'DVA composite readiness score' },
              { label: 'Governance Readiness',     value: 'Ready',                                                              sub: 'Structured for leadership review' },
            ] as const).map(({ label, value, sub }) => (
              <div
                key={label}
                style={{
                  backgroundColor: '#F0F4F8',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  padding: '14px 16px',
                }}
              >
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#717182', margin: '0 0 7px 0' }}>{label}</p>
                <p style={{ fontSize: '19px', fontWeight: 800, color: '#18365E', margin: '0 0 2px 0', lineHeight: 1.1 }}>{value}</p>
                <p style={{ fontSize: '11px', color: '#717182', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>

          {/* Sequenced initiatives — top 3 */}
          <div style={{ marginBottom: '18px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: '#18365E', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Sequenced Initiative Portfolio
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {finalists.slice(0, 3).map((f, i) => (
                <div
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    backgroundColor: i === 0 ? '#F0F4F8' : '#F8F9FB',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${i === 0 ? '#FF6E4C' : '#e5e7eb'}`,
                  }}
                >
                  <span
                    style={{
                      width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: i === 0 ? '#FF6E4C' : '#18365E',
                      color: '#fff', fontSize: '11px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {f.rank}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#18365E', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.title}
                    </p>
                    <p style={{ fontSize: '11px', color: '#717182', margin: '2px 0 0 0' }}>
                      {f.category} · {f.implementationEffort} effort · {f.timeToValue}
                    </p>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#18365E', flexShrink: 0 }}>
                    ${f.roiEstimate.low.toFixed(1)}M – ${f.roiEstimate.high.toFixed(1)}M
                  </span>
                </div>
              ))}
              {finalists.length > 3 && (
                <p style={{ fontSize: '11px', color: '#717182', margin: '4px 0 0 14px', fontStyle: 'italic' }}>
                  + {finalists.length - 3} additional initiatives sequenced in the full plan below
                </p>
              )}
            </div>
          </div>

          {/* Execution readiness statement */}
          <div
            style={{
              backgroundColor: '#18365E',
              borderRadius: '10px',
              padding: '15px 20px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
            }}
          >
            <div style={{ width: '3px', alignSelf: 'stretch', backgroundColor: '#FF6E4C', borderRadius: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#FF6E4C', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                From Prioritization to Execution
              </p>
              <p style={{ fontSize: '12px', lineHeight: 1.7, color: 'rgba(255,255,255,0.82)', margin: 0 }}>
                This plan bridges the Portfolio Strategy Report's prioritized opportunity ranking to a concrete,
                leadership-ready execution commitment — with sequencing, resource allocation, risk governance, and
                an implementation roadmap structured for immediate leadership review and board-level approval.
              </p>
            </div>
          </div>

        </div>

        {/* Thin section divider */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />

        {/* ══════════════════════════════════════════════════ */}
        {/* REPORT SECTIONS                                    */}
        {/* ══════════════════════════════════════════════════ */}
        <div className="bg-white" style={{ padding: '32px 40px' }}>

          {/* ── How to Execute This Plan ─────────────────────────── */}
          <div className="sep-atomic" style={{ marginBottom: '28px', padding: '18px 20px', backgroundColor: '#F0F4F8', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#18365E', margin: '0 0 10px 0' }}>
              How to Execute This Plan
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {[
                { label: 'Start here', text: 'Section 1 (Executive Summary) — confirm portfolio scope, total ROI, and leadership assignments.' },
                { label: 'Sequence', text: 'Section 5 (Execution Sequencing) — approve phasing and dependencies before committing resources.' },
                { label: 'Resource', text: 'Section 6 (Resource Plan) — validate FTE allocation and timeline against capacity constraints.' },
                { label: 'Govern', text: 'Section 8 (Governance) — establish decision rights, cadences, and escalation paths.' },
                { label: 'Detail', text: 'Individual Use Case Profiles contain initiative-level ROI modeling, risk registers, and feasibility assessments.' },
              ].map(({ label, text }) => (
                <div key={label} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF6E4C', flexShrink: 0, width: '72px' }}>{label}</span>
                  <span style={{ fontSize: '11px', color: '#374151', lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 1. Executive Summary */}
          <div className="sep-section sep-section-break">
            <SEPSectionExecutiveSummary
              clientName={clientName}
              finalists={finalists}
              portfolioRoiLow={portfolioRoiLow}
              portfolioRoiHigh={portfolioRoiHigh}
              executionHorizon={executionHorizon}
              quickWinCount={quickWinCount}
            />
            {/* Cross-artifact reference */}
            <div style={{ fontSize: '11px', color: '#717182', marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', lineHeight: 1.6 }}>
              Derived from the Portfolio Strategy Report. Initiative-level ROI, risk, and feasibility detail in the Use Case Profiles. Finance documentation in the CFO ROI Summary.
            </div>
          </div>

          {/* 2. Execution Strategy */}
          <div className="sep-section sep-section-break">
            <SEPSectionExecutionStrategy clientName={clientName} finalists={finalists} />
          </div>

          {/* 3. Portfolio Impact */}
          <div className="sep-section sep-section-break">
            <SEPSectionPortfolioImpact
              finalists={finalists}
              portfolioRoiLow={portfolioRoiLow}
              portfolioRoiHigh={portfolioRoiHigh}
            />
          </div>

          {/* 4. Initiative Portfolio */}
          <div className="sep-section sep-section-break">
            <SEPSectionInitiativePortfolio finalists={finalists} clientName={clientName} />
          </div>

          {/* 5. Execution Sequencing */}
          <div className="sep-section sep-section-break">
            <SEPSectionExecutionSequencing finalists={finalists} clientName={clientName} />
          </div>

          {/* 6. Resource Plan */}
          <div className="sep-section sep-section-break">
            <SEPSectionResourcePlan finalists={finalists} clientName={clientName} />
          </div>

          {/* 7. Dependencies & Risk */}
          <div className="sep-section sep-section-break">
            <SEPSectionDependenciesRisk finalists={finalists} clientName={clientName} />
          </div>

          {/* 8. Governance */}
          <div className="sep-section sep-section-break">
            <SEPSectionGovernance finalists={finalists} clientName={clientName} />
          </div>

          {/* 9. Implementation Roadmap */}
          <div className="sep-section sep-section-break">
            <SEPSectionImplementationRoadmap finalists={finalists} clientName={clientName} />
          </div>

          {/* 10. Execution Recommendation */}
          <div className="sep-section sep-section-break">
            <SEPSectionExecutionRecommendation
              finalists={finalists}
              clientName={clientName}
              portfolioRoiLow={portfolioRoiLow}
              portfolioRoiHigh={portfolioRoiHigh}
              executionHorizon={executionHorizon}
            />
          </div>

          {/* ── Report Footer ────────────────────────────── */}
          <div className="mt-12 pt-6 border-t border-[#e5e7eb] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#717182]">
                Strategic Execution Plan
              </p>
              <p className="text-[10px] text-[#FF6E4C] font-semibold">
                DVA · Adaptive Product
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#717182]">
                {clientName} · {formatDate()}
              </p>
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[#FF6E4C]">
                Early Adopter Preview
              </p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}